import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Modal from '../common/Modal';
import { Button } from '../../design-system/components/Button';
import { Text } from '../../design-system/components/Typography';
import { PaymentSettings, GeneratePaymentsRequest, GeneratePaymentsResult } from '../../types/payment';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { breakpoints } from '../../design-system/tokens/breakpoints';
import { animations } from '../../design-system/tokens/animations';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const Intro = styled(Text)`
  margin: 0;
  line-height: 1.45;
`;

const TopBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[3]} ${spacing[5]};
`;

const InlineField = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const FieldLabel = styled.label`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.text};
`;

const RequiredMark = styled.span`
  color: ${props => props.theme.colors.error};
`;

const YearInput = styled.input`
  width: 96px;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.colors.surface};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}22;
  }
`;

const OptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[4]};
`;

const CheckOption = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  cursor: pointer;
  user-select: none;
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.text};

  input {
    width: 16px;
    height: 16px;
    accent-color: ${props => props.theme.colors.primary};
    cursor: pointer;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  padding: ${spacing[4]};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  background: ${props => props.theme.colors.surfaceHover || props.theme.colors.surface};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.text};
`;

const SectionDesc = styled.p`
  margin: 0;
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.4;
`;

const AmountGrid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols ?? 2}, minmax(0, 1fr));
  gap: ${spacing[3]};

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const AmountCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const AmountLabel = styled.label`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.text};
`;

const AmountHint = styled.span`
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
  font-family: ${typography.fontFamily.primary};
  line-height: 1.25;
`;

const AmountError = styled.p`
  margin: 0;
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.error};
`;

const AmountInputWrap = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  background: ${props => props.theme.colors.surface};
  overflow: hidden;
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.fast,
    animations.easing.easeOut
  )};

  &:focus-within {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}22;
  }
`;

const EuroPrefix = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0 ${spacing[2]};
  background: ${props => props.theme.colors.surfaceHover};
  border-right: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
`;

const AmountInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  padding: ${spacing[2]};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.primary};
  color: ${props => props.theme.colors.text};
  background: transparent;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

const ResultBox = styled.div`
  padding: ${spacing[4]};
  border-radius: ${borders.radius.lg};
  border: 1px solid ${props => props.theme.colors.success}55;
  background: ${props => props.theme.colors.success}12;
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

type AmountFieldKey =
  | 'regularAmount'
  | 'studentAmount'
  | 'retiredAmount'
  | 'honoraryAmount'
  | 'institutionCategory0Amount'
  | 'institutionCategory1Amount'
  | 'institutionCategory2Amount'
  | 'institutionCategory3Amount'
  | 'institutionCategory4Amount'
  | 'institutionCategory5Amount'
  | 'institutionBenefactorAmount';

function amountToInput(value: number | undefined, fallback = 0): string {
  const n = value ?? fallback;
  return Number.isFinite(n) ? String(n) : '';
}

function parseAmountInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

interface GeneratePaymentsFormProps {
  year: number;
  settings: PaymentSettings | null;
  onClose: () => void;
  onGenerate: (request: GeneratePaymentsRequest) => Promise<GeneratePaymentsResult>;
}

const GeneratePaymentsForm: React.FC<GeneratePaymentsFormProps> = ({
  year,
  settings,
  onClose,
  onGenerate,
}) => {
  const { t } = useTranslation();
  const [yearValue, setYearValue] = useState(year);
  const [generateIndividuals, setGenerateIndividuals] = useState(true);
  const [generateInstitutions, setGenerateInstitutions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratePaymentsResult | null>(null);
  const [amounts, setAmounts] = useState<Record<AmountFieldKey, string>>({
    regularAmount: amountToInput(settings?.regularAmount, 70),
    studentAmount: amountToInput(settings?.studentAmount, 40),
    retiredAmount: amountToInput(settings?.retiredAmount, 40),
    honoraryAmount: amountToInput(settings?.honoraryAmount, 0),
    institutionCategory0Amount: amountToInput(settings?.institutionCategory0Amount, 300),
    institutionCategory1Amount: amountToInput(settings?.institutionCategory1Amount, 350),
    institutionCategory2Amount: amountToInput(settings?.institutionCategory2Amount, 410),
    institutionCategory3Amount: amountToInput(settings?.institutionCategory3Amount, 520),
    institutionCategory4Amount: amountToInput(settings?.institutionCategory4Amount, 620),
    institutionCategory5Amount: amountToInput(settings?.institutionCategory5Amount, 720),
    institutionBenefactorAmount: amountToInput(settings?.institutionBenefactorAmount, 520),
  });

  useEffect(() => {
    setYearValue(settings?.year ?? year);
    setAmounts({
      regularAmount: amountToInput(settings?.regularAmount, 70),
      studentAmount: amountToInput(settings?.studentAmount, 40),
      retiredAmount: amountToInput(settings?.retiredAmount, 40),
      honoraryAmount: amountToInput(settings?.honoraryAmount, 0),
      institutionCategory0Amount: amountToInput(settings?.institutionCategory0Amount, 300),
      institutionCategory1Amount: amountToInput(settings?.institutionCategory1Amount, 350),
      institutionCategory2Amount: amountToInput(settings?.institutionCategory2Amount, 410),
      institutionCategory3Amount: amountToInput(settings?.institutionCategory3Amount, 520),
      institutionCategory4Amount: amountToInput(settings?.institutionCategory4Amount, 620),
      institutionCategory5Amount: amountToInput(settings?.institutionCategory5Amount, 720),
      institutionBenefactorAmount: amountToInput(settings?.institutionBenefactorAmount, 520),
    });
    setAmountError(null);
    setResult(null);
  }, [settings, year]);

  const setAmount = (field: AmountFieldKey, raw: string) => {
    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
    setAmounts(prev => ({ ...prev, [field]: raw }));
    if (amountError) setAmountError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!generateIndividuals && !generateInstitutions) {
      setAmountError(t('admin.payments.generateSelectTarget'));
      return;
    }

    const parsed: Partial<Record<AmountFieldKey, number>> = {};
    for (const key of Object.keys(amounts) as AmountFieldKey[]) {
      const value = parseAmountInput(amounts[key]);
      if (value == null) {
        setAmountError(t('admin.payments.amountRequired'));
        return;
      }
      parsed[key] = value;
    }

    setAmountError(null);
    setSubmitting(true);
    try {
      const response = await onGenerate({
        year: yearValue,
        regularAmount: parsed.regularAmount!,
        studentAmount: parsed.studentAmount!,
        retiredAmount: parsed.retiredAmount!,
        honoraryAmount: parsed.honoraryAmount!,
        institutionCategory0Amount: parsed.institutionCategory0Amount!,
        institutionCategory1Amount: parsed.institutionCategory1Amount!,
        institutionCategory2Amount: parsed.institutionCategory2Amount!,
        institutionCategory3Amount: parsed.institutionCategory3Amount!,
        institutionCategory4Amount: parsed.institutionCategory4Amount!,
        institutionCategory5Amount: parsed.institutionCategory5Amount!,
        institutionBenefactorAmount: parsed.institutionBenefactorAmount!,
        generateIndividualPayments: generateIndividuals,
        generateInstitutionPayments: generateInstitutions,
      });
      setResult(response);
    } catch (err) {
      setAmountError(err instanceof Error ? err.message : t('admin.payments.generateError'));
    } finally {
      setSubmitting(false);
    }
  };

  const individualFields: { key: AmountFieldKey; labelKey: string }[] = [
    { key: 'regularAmount', labelKey: 'admin.payments.regularAmount' },
    { key: 'studentAmount', labelKey: 'admin.payments.studentAmount' },
    { key: 'retiredAmount', labelKey: 'admin.payments.retiredAmount' },
    { key: 'honoraryAmount', labelKey: 'admin.payments.honoraryAmount' },
  ];

  const institutionFields: { key: AmountFieldKey; labelKey: string; hintKey: string }[] = [
    {
      key: 'institutionCategory0Amount',
      labelKey: 'admin.payments.institutionCategory0Short',
      hintKey: 'admin.payments.institutionCategory0Hint',
    },
    {
      key: 'institutionCategory1Amount',
      labelKey: 'admin.payments.institutionCategory1Short',
      hintKey: 'admin.payments.institutionCategory1Hint',
    },
    {
      key: 'institutionCategory2Amount',
      labelKey: 'admin.payments.institutionCategory2Short',
      hintKey: 'admin.payments.institutionCategory2Hint',
    },
    {
      key: 'institutionCategory3Amount',
      labelKey: 'admin.payments.institutionCategory3Short',
      hintKey: 'admin.payments.institutionCategory3Hint',
    },
    {
      key: 'institutionCategory4Amount',
      labelKey: 'admin.payments.institutionCategory4Short',
      hintKey: 'admin.payments.institutionCategory4Hint',
    },
    {
      key: 'institutionCategory5Amount',
      labelKey: 'admin.payments.institutionCategory5Short',
      hintKey: 'admin.payments.institutionCategory5Hint',
    },
    {
      key: 'institutionBenefactorAmount',
      labelKey: 'admin.payments.institutionBenefactorShort',
      hintKey: 'admin.payments.institutionBenefactorHint',
    },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('admin.payments.generateTitle')}
      maxWidth="820px"
      footer={
        <>
          <Button variant="outline" type="button" onClick={onClose} disabled={submitting}>
            {result ? t('common.close') : t('common.cancel')}
          </Button>
          {!result && (
            <Button
              variant="primary"
              type="submit"
              form="generate-payments-form"
              disabled={submitting}
            >
              {submitting
                ? t('admin.payments.generateWorking')
                : t('admin.payments.generateAction')}
            </Button>
          )}
        </>
      }
    >
      <Form id="generate-payments-form" onSubmit={handleSubmit}>
        <Intro size="sm" color="secondary">
          {t('admin.payments.generateIntro')}
        </Intro>

        <TopBar>
          <InlineField>
            <FieldLabel htmlFor="generate-payments-year">
              {t('payments.year')}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <YearInput
              id="generate-payments-year"
              type="number"
              min={2000}
              max={2100}
              value={yearValue}
              onChange={e => setYearValue(parseInt(e.target.value, 10) || year)}
              required
              disabled={!!result}
            />
          </InlineField>
        </TopBar>

        <OptionsRow>
          <CheckOption>
            <input
              type="checkbox"
              checked={generateIndividuals}
              onChange={e => setGenerateIndividuals(e.target.checked)}
              disabled={!!result}
            />
            {t('admin.payments.generateIndividuals')}
          </CheckOption>
          <CheckOption>
            <input
              type="checkbox"
              checked={generateInstitutions}
              onChange={e => setGenerateInstitutions(e.target.checked)}
              disabled={!!result}
            />
            {t('admin.payments.generateInstitutions')}
          </CheckOption>
        </OptionsRow>

        {amountError && <AmountError role="alert">{amountError}</AmountError>}

        <Section>
          <SectionTitle>{t('admin.payments.individualAmountsTitle')}</SectionTitle>
          <SectionDesc>{t('admin.payments.individualAmountsDesc')}</SectionDesc>
          <AmountGrid $cols={4}>
            {individualFields.map(field => (
              <AmountCell key={field.key}>
                <AmountLabel htmlFor={`gen-${field.key}`}>{t(field.labelKey)}</AmountLabel>
                <AmountInputWrap>
                  <EuroPrefix aria-hidden>€</EuroPrefix>
                  <AmountInput
                    id={`gen-${field.key}`}
                    type="text"
                    inputMode="decimal"
                    value={amounts[field.key]}
                    onChange={e => setAmount(field.key, e.target.value)}
                    onFocus={e => e.target.select()}
                    required
                    disabled={!!result}
                  />
                </AmountInputWrap>
              </AmountCell>
            ))}
          </AmountGrid>
        </Section>

        <Section>
          <SectionTitle>{t('admin.payments.institutionAmountsTitle')}</SectionTitle>
          <SectionDesc>{t('admin.payments.institutionAmountsDesc')}</SectionDesc>
          <AmountGrid $cols={3}>
            {institutionFields.map(field => (
              <AmountCell key={field.key}>
                <AmountLabel htmlFor={`gen-${field.key}`}>{t(field.labelKey)}</AmountLabel>
                <AmountHint>{t(field.hintKey)}</AmountHint>
                <AmountInputWrap>
                  <EuroPrefix aria-hidden>€</EuroPrefix>
                  <AmountInput
                    id={`gen-${field.key}`}
                    type="text"
                    inputMode="decimal"
                    value={amounts[field.key]}
                    onChange={e => setAmount(field.key, e.target.value)}
                    onFocus={e => e.target.select()}
                    required
                    disabled={!!result}
                  />
                </AmountInputWrap>
              </AmountCell>
            ))}
          </AmountGrid>
        </Section>

        {result && (
          <ResultBox role="status">
            <Text size="sm" color="text">
              {t('admin.payments.generateResultTitle', { year: result.year })}
            </Text>
            <Text size="sm" color="secondary">
              {t('admin.payments.generateResultIndividuals', {
                created: result.individualsCreated,
                skipped: result.individualsSkipped,
              })}
            </Text>
            <Text size="sm" color="secondary">
              {t('admin.payments.generateResultInstitutions', {
                created: result.institutionsCreated,
                skipped: result.institutionsSkipped,
              })}
            </Text>
          </ResultBox>
        )}
      </Form>
    </Modal>
  );
};

export default GeneratePaymentsForm;
